import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  Building2, 
  Plus, 
  Search, 
  ExternalLink,
  Users,
  Calendar
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'

interface Organization {
  id: string
  name: string
  slug: string
  createdAt: Date
  enabledFeatures?: string[]
  memberCount?: number
}

export function AdminOrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const db = getFirebaseDb()
        const orgsRef = collection(db, 'organizations')
        const orgsQuery = query(orgsRef, orderBy('createdAt', 'desc'))
        const orgsSnapshot = await getDocs(orgsQuery)
        
        const orgsData = await Promise.all(
          orgsSnapshot.docs.map(async (doc) => {
            const data = doc.data()
            
            return {
              id: doc.id,
              name: data.name,
              slug: data.slug,
              createdAt: data.createdAt?.toDate() || new Date(),
              enabledFeatures: data.enabledFeatures || [],
              memberCount: 0 // Simplified for now
            }
          })
        )
        
        setOrganizations(orgsData)
      } catch (error) {
        console.error('Error fetching organizations:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchOrganizations()
  }, [])

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.slug.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Organizations</h1>
          <p className="text-muted-foreground">
            Manage all organizations on the platform
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Organization
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search organizations..."
          className="pl-10"
        />
      </div>

      {/* Organizations List */}
      {filteredOrganizations.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Organizations Found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {searchQuery 
                  ? 'No organizations match your search' 
                  : 'No organizations have been created yet'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredOrganizations.map((org) => (
            <Card key={org.id}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {org.name}
                    <Link to={`/${org.slug}/assist`}>
                      <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </Link>
                  </CardTitle>
                  <CardDescription>/{org.slug}</CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/admin/organizations/${org.id}`}>
                    Manage
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Created {org.createdAt.toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    {org.memberCount || 0} members
                  </div>
                </div>
                {org.enabledFeatures && org.enabledFeatures.length > 0 && (
                  <div className="flex gap-2 mt-3">
                    {org.enabledFeatures.map((feature) => (
                      <Badge key={feature} variant="secondary">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
